/**
 * Capture the current screen in the background once the report widget is
 * open (ISS-485).
 *
 * The old widget made the user click "Screenshot", hid itself, and blocked on
 * html2canvas before they could type — measured ~0.6s on a report-sized page
 * on a fast machine (client/scripts/measure-report-capture.mjs), plus the
 * extra click. Now the widget paints first, the capture starts right after,
 * and the widget is EXCLUDED from the capture (`ignoreElements`) instead of
 * being hidden, so nothing flashes and the user can type immediately.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';

export type CaptureStatus = 'idle' | 'capturing' | 'done' | 'failed';

export function useBackgroundScreenCapture(active: boolean, excludeRef: RefObject<HTMLElement | null>) {
  const [capture, setCapture] = useState<string | null>(null);
  const [status, setStatus] = useState<CaptureStatus>('idle');
  // Only the capture started by the CURRENT open may write state.
  const generation = useRef(0);

  useEffect(() => {
    if (!active) {
      // Never hold a capture while capturing is off (e.g. the widget
      // re-opened on an HR / payroll page) — #5163 review L3.
      generation.current++;
      setCapture(null);
      setStatus('idle');
      return undefined;
    }
    const gen = ++generation.current;
    setCapture(null);
    setStatus('capturing');
    // A macrotask, so the widget paints before html2canvas takes the thread.
    const handle = window.setTimeout(async () => {
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(document.body, {
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          // 1x, not the display's pixel ratio: on a 2x laptop the default
          // renders 4x the pixels — measured 922ms / 5.8MB vs 645ms / 2.3MB
          // on a report-sized page (scripts/measure-report-capture.mjs).
          // Triage needs a readable picture, and a 5.8MB capture alone eats
          // over half the callable's request limit and slows the send.
          scale: 1,
          ignoreElements: (el: Element) => {
            const widget = excludeRef.current;
            return !!widget && (el === widget || widget.contains(el));
          },
        });
        if (gen !== generation.current) return;
        // JPEG at 70% quality is ~60% smaller than PNG.
        setCapture(canvas.toDataURL('image/jpeg', 0.7));
        setStatus('done');
      } catch (err) {
        // The report still sends without it; the tray shows "Could not capture".
        console.warn('[BugReportWidget] screen capture failed', err);
        if (gen === generation.current) setStatus('failed');
      }
    }, 0);
    return () => {
      window.clearTimeout(handle);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- generation is a plain counter, not a DOM node: reading the LATEST value at cleanup is the point (it cancels any capture still in flight)
      generation.current++;
    };
  }, [active, excludeRef]);

  return { capture, setCapture, status };
}
